import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import {
  fetchOverAllScorebydate as fetchOverAllScoreByDateService,
  fetchOverallMainControlScores as fetchOverallMainControlScoresService,
  fetchOverAllScoreByAssessment as fetchOverAllScoreByAssessmentService,
} from "services/modules/cap/assessmentservice";
import { fetchAssessmentDataByAssessmentId as fetchAssessmentDataByAssessmentIdService } from "services/modules/cap/standardservice";
import { fetchCompanyProfile as fetchCompanyProfileService } from "services/modules/idp/company";

import { LinearProgress, Chip, Select, MenuItem, CircularProgress } from "@mui/material";
import html2pdf from "html2pdf.js";
import ApexCharts from "react-apexcharts";
import EChartsReact from 'echarts-for-react';

import AuditTrialPage from "./AuditTrialPage.jsx";
import ReportStatusPage from "./ReportStatusPage.jsx";
import "../styles/cap.css";


const CompleteReportDashboardPage = () => {
  // useLocation hook
  const location = useLocation();

  // url params
  const urlparams = location.state;

  // Redux selectors
  const selectedCompanyIDContainer = useSelector((state) => state.auth.companyData.companyid);
  const dateContainer = useSelector((state) => state.auth.user.company.createdAt);
  const authenticatedUserContainer = useSelector((state) => state.auth.user);
  
  // State variables
  const [assessmentId, setAssessmentId] = useState("");
  const [groupedArray, setGroupedArray] = useState([]);
  const [controlWiseCoreList, setControlWiseCoreList] = useState([]);
  const [assessmentData, setAssessmentData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [startYear, setStartYear] = useState("");
  const endYear = new Date().getFullYear();
  const [yearsList, setYearsList] = useState([]);
  const [series, setSeries] = useState([]);
  const [overallScore, setOverallScore] = useState(0);
  const [assessmentIds, setAssessmentIds] = useState(Array(12).fill(""));
  const [overAllPercentage, setOverAllPercentage] = useState(0);
  const [reportStatusPopup, setReportStatusPopup] = useState(false);
  const elementRef = useRef(null);
  const [auditTrailPopup, setAuditTrailPopup] = useState(false);
  const [loading,setLoading] = useState(false);


  const standardIconMap = {"CIS": "../assets/icons/standard-cis.png","ISO-27001": "../assets/icons/standard-iso-27001.png","NIST": "../assets/icons/standard-nist.png",
  "TISAX": "../assets/icons/standard-tisax.png","COBIT": "../assets/icons/standard-cobit.png","NIS 2.0": "../assets/icons/standard-nis2.png",
  "Ransomware": "../assets/icons/standard-ransomware.png","Supplier Risk": "../assets/icons/standard-supplier-risk.png","Data Protection": "../assets/icons/standard-data-protection.png",};
  const chartOptions = {
    
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const score = series[seriesIndex][dataPointIndex];
        const assessmentId = assessmentIds[dataPointIndex];
        const monthNames = ["Jan","Feb","Mar","Apr","May","June","July","Aug","Sep","Oct","Nov","Dec"];
        const monthName = monthNames[dataPointIndex];
        const modifiedScore = `<span class="score">&#9679;</span> Score : <b>${score}</b>`;
        const modifiedAssessmentIds = `<span class="score">&#9679;</span> Assessment ID : <b>${assessmentId}</b>`;
        if (assessmentId !== "") {
          return `<div class="tooltip-custom">
                    <span class="month-header">${monthName}</span><br/>
                    <hr/>
                    <span>${modifiedScore}</span><br/>
                    <span>${modifiedAssessmentIds}</span>
                  </div>`;
        } else {
          return `<div class="tooltip-custom">
                    <span class="month-header">${monthName}</span><br/>
                    <hr/>
                    <span>${modifiedScore}</span>
                  </div>`;
        }
      },
    },
    chart: {
      height: 280,
      with: "100%",
      type: "area",
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 3,
      curve: "smooth",
    },
    toolbar: {
      show: false,
      tools: {
        download: false,
      },
    },
    grid: {
      row: {
        colors: ["#f3f3f3", "transparent"],
        opacity: 0.5,
      },
    },
    xaxis: {
      categories: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
    },
    colors: ["#008FFB"],
  };

  // This method returns emoji based on final score
  function getEmoji(value) {
    switch (true) {
      case value >= 80:
        return require("../../../assets/emoji_1.png");
      // case value >= 60:
      //   return require("../../../assets/emoji_2.png");
      case value >= 40:
        return require("../../../assets/emoji_3.png");
      // case value >= 20:
      //   return require("../../../assets/emoji_4.png");
      case value >= 0:
        return require("../../../assets/emoji_5.png");
      default:
        return require("../../../assets/emoji_1.png");
    }
  }

  // Gauge options
  let gaugeOptions = {
    tooltip: {
      responsive: true,
      position: "top",
      formatter: params => {
        const value = params.value
        if (value === 0) {
          return 'A'
        } else if (value > 0 && value < 3.99) {
          return 'B'
        } else if (value >= 4 && value < 6.99) {
          return 'C'
        } else if (value >= 7 && value < 8.99) {
          return 'D'
        } else if (value >= 9) {
          return 'F'
        } else {
          return 'A'
        }
      },
      padding: 5,
      transitionDuration: 0,
      hideDelay: 100,
      backgroundColor: "#fff",
      borderColor: "#c8e2f7",
      borderWidth: "0.2",
      textStyle: {
        color: "#414143",
        fontSize: 14,
        fontFamily: "Montserrat",
        fontWeight: 600,
      },
    },
    series: [
      {
        name: "Risk Posture",
        type: "gauge",
        startAngle: 200,
        endAngle: -20,
        min: 1,
        max: 10,
        splitNumber: 3,
        radius: "80%",
        center: ["50%", "50%"],
        axisLine: {
          lineStyle: {
            width: 40,
            color: [
              [0.337, "#db3832"],
              // [0.4, "#e56c39"],
              [0.669, "#fcdd50"],
              // [0.8, "#9ac356"],
              [1, "#41914d"],
            ],
          },
        },
        pointer: {
          width: 12,
          length: "50%",
          distance: -500,
          offsetCenter: [0, "-15%"],
          itemStyle: {
            color: "#414143",
            shadowBlur: 5,
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          distance: -200,
          length: 201,
          lineStyle: {
            color: "#fff",
            width: 5,
          },
        },
        axisLabel: {
          color: "#fff",
          distance: 200,
          fontSize: 20,
          show: false,
        },
        detail: {
          show: true,
          valueAnimation: true,
          formatter: function () {
            return "";
          },
          color: "inherit",
          backgroundColor: {
            image: getEmoji(overAllPercentage),
          },
          height: 35,
          width: 25,
          offsetCenter: [0, "45%"],
        },
        title: {
          offsetCenter: [0, '80%'],
          fontSize: 18,
          fontWeight: 'Bold',
          color: '#0e0a65'
        },
        data: [
          {
            value: overallScore,
          },
        ],
      },
    ],
  };

  // useEffect sets the assessment id,fetches radar graph data,assessment data and meter chart data
  useEffect(() => {
    setAssessmentId(urlparams.assessmentid);
    getRadardata(urlparams.assessmentid);
    fetchAssessmentData();
    overallmeterchartdata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // useEffect hook fetches all the year options for year selection dropdown
  useEffect(() => {
    const years = [];
    for (let i = endYear; i >= startYear; i--)
      years.push(i);
    setYearsList(years);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startYear]);

  useEffect(() => {
    groupedRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlWiseCoreList]);

  // useEffect hook sets the start year as company created date
  useEffect(() => {
    const fetchCompanyData = async () => {
      setLoading(true);
      if (authenticatedUserContainer?.roles[0]?.slug === "user") {
        setStartYear(new Date(dateContainer).getFullYear());
      } else {
        const response = await fetchCompanyProfileService(selectedCompanyIDContainer);
        setStartYear(new Date(response?.createdAt)?.getFullYear());
      }
    };
    if (selectedCompanyIDContainer !== null) 
      fetchCompanyData();
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyIDContainer]);
  
  // Fetches the subcontrols for each control and groups them into a single row
  const groupedRows = () => {
    const rows = controlWiseCoreList;
    const groupSize = 5;
    const groupedArray = [];
    for (let i = 0; i < rows.length; i += groupSize) {
      groupedArray.push(rows.slice(i, i + groupSize));
    }
    setGroupedArray(groupedArray);
  };

  // Fetches radar data
  const getRadardata = async (id) => {
    try {
      setLoading(true);
      let params = { assessment_id: id };
      let response = await fetchOverallMainControlScoresService(params);
      if (response.data.OverAllscore.length > 0) {
        setControlWiseCoreList(response.data.OverAllscore);
      }
    } catch (error) {
    }finally{
      setLoading(false);
    }
  };

  // Fetches assessment data based on assessment id
  const fetchAssessmentData = async () => {
    try {
      setAssessmentData([]);
      let params = { assessment_id: urlparams.assessmentid };
      let assessmentsresponse = await fetchAssessmentDataByAssessmentIdService({params});
      if (assessmentsresponse.data.length > 0) {
        setAssessmentData(assessmentsresponse.data);
        fetchMonthWiseStandardScoreByYear(selectedYear,assessmentsresponse.data);
      }
    } catch (e) {
    }
  };

  // Fetches monthwise score
  const fetchMonthWiseStandardScoreByYear = async (selectedyear,assessmentData,) => {
    try {
      setLoading(true);
      setSeries([]);
      let startDate = selectedyear + "-01-01";
      let endDate = selectedyear + "-12-31";
      let params = {startdate: startDate,enddate: endDate,company: assessmentData[0].company,standard: assessmentData[0].standard};
      let overallscoreresponse = await fetchOverAllScoreByDateService({params});
      if (overallscoreresponse.data.data.length > 0) {
        let latestSeries = [{name: "Score",data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]}];
        overallscoreresponse.data.data.forEach((e, index) => {
          let recordMonth = new Date(e.date).getMonth();
          if (recordMonth >= 1 && recordMonth <= 12) {
            latestSeries[0].data[recordMonth] = Math.round(e.score);
            assessmentIds[recordMonth] = e.assessmentId;
          }
        });
        setSeries(latestSeries);
        setAssessmentIds(assessmentIds);
      }
    } catch (e) {}
    finally{
      setLoading(false);
    }
  };
  
  // Fetches over all meter chart data based on assessment id
  const overallmeterchartdata = async () => {
    try {
      let params = { assessment_id: urlparams.assessmentid };
      let response = await fetchOverAllScoreByAssessmentService(params);
      if (response.data.OverAllPercentage) {
        setOverAllPercentage(Math.round(response.data.OverAllPercentage));
      }
    } catch (e) {}
  };
  
  useEffect(() => {
    if (overAllPercentage >= 70) {
      setOverallScore(9);
    } else if (overAllPercentage >= 30) {
      setOverallScore(5);
    } else {
      setOverallScore(1);
    }
  }, [overAllPercentage]);
  
  // Downloads the complete reports dashboard for an assessment in PDF Format
  const exportToPDF = () => {
    const element = elementRef.current;
    const opt = {
      margin: [0, 1, 1, 1],
      filename: "RHYM-CAP-report.pdf",
      image: { type: "jpeg", quality: 2 },
      pageBreak: { mode: "css", before: "#nextpage1" },
      jsPDF: { unit: "in", format: "A2" },
      html2canvas: {
        scale: 4,
        dpi: 72,
        letterRendering: true,
        onclone: (element) => {
          const svgElements = Array.from(element.querySelectorAll("svg"));
          svgElements.forEach((s) => {
            const bBox = s.getBBox();
            s.setAttribute("x", bBox.x);
            s.setAttribute("y", bBox.y);
            s.setAttribute("width", bBox.width);
            s.setAttribute("height", bBox.height);
          });
        },
      },
    };
    html2pdf().set(opt).from(element).save();
  };
  
  // Handles change in selected year dropdown
  const onChange = async (event) => {
    setSelectedYear(event.target.value);
    fetchMonthWiseStandardScoreByYear(event.target.value, assessmentData);
  };

  return (
    <div id="buttondiv" ref={elementRef} className="my-4">
      <div className="row flex justify-space mb-2">
        <div id="printlogo" className="auto-width">
          <h5>RHYM Compliance Portal</h5>
        </div>
        <div className="flex auto-width">
          <div className="m-1">
            <button
              className="go-back-button mr-sm report-status-btn"
              onClick={() => setReportStatusPopup(!reportStatusPopup)}
            >
              What is report status?
            </button>
          </div>
          {reportStatusPopup && (
            <ReportStatusPage
              toggleReportStatus={() =>
                setReportStatusPopup(!reportStatusPopup)
              }
            />
          )}
          <div className="m-1">
            <button
              className="approve-button "
              id="buttondiv"
              onClick={() => exportToPDF()}
            >
              <i className="fas fa-download fa-sm text-white-50"></i>Download
              report
            </button>
          </div>
        </div>
      </div>
      <div>
        <div className="row wrapper-row complete-reports-dashboard-alignment mb-2">
          <div className="header-div auto-width">
            <div className="dailogBox">
              {assessmentData.length > 0 && (
                <>
                  {standardIconMap[assessmentData[0].standardname] && (
                    <img
                      src="../assets/icons/standard-cis.png"
                      className="standard-icon-dashboard"
                      alt="view"
                      draggable="false"
                    />
                  )}
                </>
              )}
              {assessmentData.length > 0 && (
                <div className="assessment-cont">
                  <h6>
                    <b>{assessmentData[0].standardname}</b>
                  </h6>
                  <div className="smaller">{assessmentData[0].acronym}</div>
                </div>
              )}
            </div>
          </div>
          <div className="header-div custom-width">
            <div>
              <b>Company name :</b>
              <span className="smaller">{assessmentData.length > 0 && assessmentData[0].companyname} </span>
            </div>
            <div>
              <b>Report id : </b>
              <span className="smaller">{assessmentId}</span>
            </div>
            <div>
              <b>Last updated date : </b>
              <span className="smaller">{assessmentData.length > 0 &&assessmentData[0].approved_date.split("T")[0]}</span>
            </div>
          </div>
          <div className="header-div custom-width">
            <div>
              <b>Report status : </b>
              <span>{assessmentData.length > 0 && assessmentData[0].status}</span>
            </div>
            <div>
              <b>Approved by : </b>
              <span>{assessmentData.length > 0 && assessmentData[0].requested_by_name}</span>
            </div>
            <div>
              <b>User name : </b>
              <span>{assessmentData.length > 0 && assessmentData[0].username}</span>
            </div>
          </div>
          <div className="col header-div flex justify-center cell-items">
            <button
              className="approve-button"
              type="button"
              onClick={() => setAuditTrailPopup(!auditTrailPopup)}
            >
              View audit trail
            </button>
            {auditTrailPopup && (
              <AuditTrialPage
                toggleAuditTrial={() => setAuditTrailPopup(!auditTrailPopup)}
                assessmentId={assessmentId}
              />
            )}
          </div>
        </div>
        <div className="row wrapper-row rounded-10 md-sm">
          <div className="col-xs-12 col-sm-12 col-md-12 col-lg-4">
           
            <div className=" text-center integrated-dashboard-bg">
              <div>
                <div className="risk-posture-title">Overall Score</div>
                <div className="gauge-chart">
                  {loading ? ( // Display CircularProgress when loading is true
                    <div className="text-center mt-4">
                      <CircularProgress  size={60}/>
                    </div>
                  ) : (
                    <div className="chart-and-legend">
                      <EChartsReact
                        className='vchart'
                        option={gaugeOptions}
                        notMerge={true}
                        lazyUpdate={true}
                        style={{ height: '300px', width: '250px', marginLeft: '5%' }}
                      />

                      <div className="mx-4 legend-cap">
                        <div className="legend-item">
                          <span className="legend-color high-risk"></span>
                          Urgent Attention
                        </div>
                        <div className="legend-item">
                          <span className="legend-color moderate-risk"></span>
                          Needs Improvement
                        </div>
                        <div className="legend-item">
                          <span className="legend-color no-risk"></span>
                          Doing Well
                        </div>
                      </div>
                      
                    </div>

                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="col-xs-12 col-sm-12 col-md-12 col-lg-8"> 
            <div id="chart" className="scoreovertime-class">
              <div style={{height:'320px'}}>
                {loading ? 
                  <div className="d-flex justify-content-center align-items-center">
                    <CircularProgress size={80} style={{marginTop:'100px'}}/> 
                  </div>
                : (
                  <div>
                    <div className="row justify-space">
                      <div className="auto-width">
                        <label className="text">Score trend analysis</label>
                        <img
                          src="../assets/icons/expand.svg"
                          alt="expand-icon"
                          draggable="false"
                          className="w-10"
                        />
                      </div>
                      <div className="auto-width pt-1">
                        <Select
                          variant="outlined"
                          size="small"
                          value={selectedYear}
                          onChange={(e) => onChange(e)}
                          displayEmpty
                          className="no-margin"
                          inputProps={{ "aria-label": "Select a year" }}
                        >
                          <MenuItem value="" disabled>
                            Select a year
                          </MenuItem>
                          { yearsList?.map((year) => (
                            <MenuItem value={year} key={year}>
                              {year}
                            </MenuItem>
                          ))}
                        </Select>
                      </div>
                    </div>
                    <ApexCharts
                      type="line"
                      height={280}
                      options={chartOptions}
                      series={series}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="row wrapper-row rounded-10">
          {groupedArray.length > 0 &&
            groupedArray.map((group, index) => (
              <div
                key={index}
                className="group col-xs-12 col-sm-12 col-md-12 col-lg-6"
              >
                <div className="score-container">
                  <div>
                    {index === 0 && (
                      <label className="text">Control wise score</label>
                    )}
                    {index !== 0 && <div className="mb-4"></div>}
                  </div>
                  {group.map((row) => (
                    <div key={row._id} className="item full-width-field p-2">
                      {row.category}
                      <div className="control-container">
                        <LinearProgress
                          variant="determinate"
                          value={parseInt(row.score) }
                          className={`control-progress ${parseInt(row.score) < 40 ? "red-color" : parseInt(row.score) >=40 && parseInt(row.score) < 75 ? "orange-color" : "green-color"}`}
                        />
                        <div
                          style={{
                            position: "absolute",
                            left: `${parseInt(row.score) > 92 ? 92 : parseInt(row.score)}%`,
                            top: 0,
                          }}
                        >
                          <Chip
                            className={`score-progress ${parseInt(row.score) < 40 ? "high-cap" : parseInt(row.score) >= 40 && parseInt(row.score)< 75 ? "moderate-cap" : "low-cap"}`}
                            label={`${parseInt(row.score)}/100`}
                          />
                          <div className="control-score">
                            <span>{`${parseInt(row.score)}/100`}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default CompleteReportDashboardPage;
